import { first, includes, toInteger } from 'es-toolkit/compat';
/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

function propertyWithDefault<T>(name: string, defaultValue: T): T {
  const envValue = import.meta.env[`VITE_${name}`] ?? import.meta.env[name];
  const finalValue = window.__TOCK__?.[name] ?? envValue ?? defaultValue;

  if (typeof defaultValue === 'boolean' && typeof finalValue === 'string') {
    return (finalValue === 'true') as any as T;
  }

  return finalValue as T;
}

function markSettingsBootstrapError(message: string) {
  (window as any).__TOCK_SETTINGS_BOOT_ERROR__ = message;
}

export function isProductionHostname(hostname: string) {
  return hostname === 'dashboard.exploretock.com';
}

export function isLocalHostname(hostname: string) {
  return (
    includes(hostname, 'localhost') ||
    includes(hostname, '127.0.0.1') ||
    hostname.startsWith('192.168.') ||
    includes(hostname, '10.8.') ||
    includes(hostname, '.local') ||
    (hostname.startsWith('100.') &&
      toInteger(hostname.split('.')[1]) >= 64 &&
      toInteger(hostname.split('.')[1]) <= 127)
  );
}

const hostname = first(window.location.hostname.split(':')) ?? '';
if (!window.__TOCK__ && !isLocalHostname(hostname)) {
  markSettingsBootstrapError('Cannot load Tock, no settings received');
}

if (!isLocalHostname(hostname) && import.meta.env.VITE_APP_ENV !== 'preview') {
  if (
    (includes(window.__TOCK__?.API_BASE, '-staging') && !includes(hostname, '-staging')) ||
    (includes(window.__TOCK__?.API_BASE, '-rc') && !includes(hostname, '-rc'))
  ) {
    markSettingsBootstrapError('Cannot load Tock, attempting to load incorrect staging settings');
  }

  if (includes(window.__TOCK__?.API_BASE, '-demo') && !includes(hostname, '-demo')) {
    markSettingsBootstrapError('Cannot load Tock, attempting to load incorrect demo settings');
  }
}

// These come from the index.html template during webpack compilation.
const isBrowser = window.BROWSER;
const isCheckin = window.CHECKIN;
const isDelivery = window.DELIVERY;

function getApiBase() {
  if (import.meta.env.MODE === 'test') {
    return `http://example.com`;
  }
  if (import.meta.env.VITE_LOCAL_SERVER === 'true') {
    return `http://${hostname}:6060/api`;
  } else {
    return 'https://dashboard-staging.exploretock.com/api';
  }
}

const isDevelopment = import.meta.env.MODE !== 'test' && import.meta.env.DEV;

export default {
  // To use your local server, set the system environment variable LOCAL_API_SERVER=true
  API_BASE: propertyWithDefault('API_BASE', getApiBase()),

  // The URL base for Braintree transactinons
  BRAINTREE_TRANSACTION_URL_BASE: propertyWithDefault(
    'BRAINTREE_TRANSACTION_URL_BASE',
    'https://sandbox.braintreegateway.com/merchants/36rgt8zsxfj4378f/transactions'
  ),

  BROWSER: isBrowser,

  CHECKIN: isCheckin,

  DELIVERY: isDelivery,

  CONSUMER_APP_URL: propertyWithDefault(
    'CONSUMER_APP_URL',
    import.meta.env.DEV ? `http://${hostname}:7000/api` : 'https://staging.exploretock.com'
  ),

  DASHBOARD_APP_URL: propertyWithDefault(
    'DASHBOARD_APP_URL',
    import.meta.env.DEV ? `http://${hostname}:8080` : 'https://dashboard-staging.exploretock.com'
  ),

  // To enable all features by default, set the system variable ENABLE_FEATURES_BY_DEFAULT=true
  ENABLE_FEATURES_BY_DEFAULT: propertyWithDefault(
    'ENABLE_FEATURES_BY_DEFAULT',
    import.meta.env.VITE_ENABLE_FEATURES_BY_DEFAULT === 'true'
  ),

  FULL_STORY_ORG_ID: '2EFSJ',

  GA_PROPERTY_ID: propertyWithDefault('GA_PROPERTY_ID', 'UA-57200812-16'),
  FACEBOOK_PIXEL_ID: propertyWithDefault('FACEBOOK_PIXEL_ID', '1258095530951991'),
  AMPLITUDE_KEY: propertyWithDefault('AMPLITUDE_KEY', 'b69ff8449545449ecefb9e57314a1bc3'),
  // The Google client API key to use for requests to Google services.
  GOOGLE_API_KEY: propertyWithDefault('GOOGLE_API_KEY', 'AIzaSyCPd-xSabI7QOfKX5NxVVup6bPfcovwFiQ'),

  GOOGLE_MAPS_KEY: propertyWithDefault(
    'GOOGLE_MAPS_KEY',
    'AIzaSyAxNcI-gFR7nGo_MNN5Z644SXJvISYiRDw'
  ),

  IS_DEVELOPMENT: isDevelopment,

  LAST_TIME_IN_BUSINESS_DAY: '04:00',

  LOG_LEVEL: propertyWithDefault<LogLevel>('LOG_LEVEL', 'NETWORKING'),

  PLAID_ENVIRONMENT: propertyWithDefault('PLAID_ENVIRONMENT', 'sandbox'),
  PLAID_PUBLIC_KEY: propertyWithDefault('PLAID_PUBLIC_KEY', '2ef951951a9b169b13869c733a889e'),

  // Rollbar Config info
  ROLLBAR_ACCESS_TOKEN: propertyWithDefault(
    'ROLLBAR_ACCESS_TOKEN',
    '35cc341eb7fd4d84946390431abd5292'
  ),
  ROLLBAR_ENV: propertyWithDefault('ROLLBAR_ENV', 'dev'),

  STRIPE_PUBLIC_KEY: propertyWithDefault(
    'STRIPE_PUBLIC_KEY',
    'pk_test_51Tg5QQK0bopwblSlqf6KOFrjVJNvKDaEYOlDJcHurinYxJEUFUHogEH812ERz6QBXrZNvFQpJk4aQ0dtyAIox5Yq00RhYnuRLx'
  ),
  STRIPE_CONNECT_KEY: propertyWithDefault(
    'STRIPE_CONNECT_KEY',
    'ca_UfQHl3Dx5zCjUaYlMfShisqO3JGD279q'
  ),

  TOCK2US_GA_PROPERTY_ID: propertyWithDefault('GA_PROPERTY_ID', null),

  WSS_BASE: propertyWithDefault(
    'WSS_BASE',
    import.meta.env.VITE_LOCAL_SERVER === 'true'
      ? `ws://${hostname}:6061/ws`
      : 'wss://dashboard-staging.exploretock.com/ws'
  ),

  //Datadog Config Info
  DATADOG_CLIENT_TOKEN: propertyWithDefault(
    'DATADOG_CLIENT_TOKEN',
    'pubb55f31573b5e4b7fad5f05527067cc6c'
  ),
  DATADOG_DASHBOARD_APP_ID: propertyWithDefault(
    'DATADOG_DASHBOARD_APP_ID',
    'bbaf38fe-54eb-4996-907a-de82dc8ba18b'
  ),
  DATADOG_ENV: propertyWithDefault('DATADOG_ENV', 'dev'),
};
